/**
 * 10.5V — CLIMA. Chuva (e tempestade com relâmpago) que aparece e passa sozinha, seguindo o
 * player para custar pouco: uma InstancedMesh de gotas dentro de uma caixa ao redor dele.
 * A tempestade devolve um "flash" que o Renderer usa para clarear a cena por um instante.
 * Regiões chuvosas: Terras Fraturadas e (principalmente) Domínio da Tempestade.
 */
import * as THREE from "three";
import { quality } from "../core/Quality";
import { randRange } from "../utils/math";

export type WeatherKind = "clear" | "rain" | "storm";

// 10.9HY — a chuva ignorava a escala de partículas do preset: 240 matrizes reescritas na CPU
// TODO quadro, com frustumCulled=false (sempre desenhada). Agora o preset manda.
const DROPS_BASE = 240;
const dropCount = (): number => Math.max(40, Math.round(DROPS_BASE * quality.values.particleScale));
const BOX = 26; // metros ao redor do player
const _m = new THREE.Object3D();

/** Chance de cada clima por região (o resto é tempo limpo). */
const CHANCE: Record<string, { rain: number; storm: number }> = {
  forgottenForest: { rain: 0.25, storm: 0.05 },
  shatteredWilds: { rain: 0.3, storm: 0.12 },
  tempestReach: { rain: 0.35, storm: 0.35 },
};

export class Weather {
  private readonly mesh: THREE.InstancedMesh;
  /** 10.9HY — quantas gotas este preset usa (fixado na construção). */
  private readonly dropCap = dropCount();
  private readonly drops: { x: number; y: number; z: number; sp: number }[] = [];
  private kind: WeatherKind = "clear";
  private timer = 50; // s até a próxima mudança de tempo
  private flash = 0;
  private strikeIn = 0;
  private regionId = "forgottenForest";
  private intensity = 0; // 0..1 (transição suave ao começar/parar de chover)
  /** 10.9BS — relâmpago AMBIENTE do Domínio da Tempestade: mesmo sem chuva, o céu pisca a cada 20–40 s. */
  private ambientStrikeIn = 25;

  constructor(scene: THREE.Scene) {
    const mat = new THREE.MeshBasicMaterial({ color: 0xdcecff, transparent: true, opacity: 0.62, depthWrite: false });
    this.mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(0.075, 1.9, 0.075), mat, this.dropCap);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    for (let i = 0; i < this.dropCap; i++) {
      this.drops.push({ x: randRange(-BOX, BOX), y: randRange(0, 18), z: randRange(-BOX, BOX), sp: randRange(24, 34) });
    }
    scene.add(this.mesh);
  }

  get current(): WeatherKind {
    return this.kind;
  }
  setRegion(id: string): void {
    this.regionId = id;
    this.timer = Math.min(this.timer, 12); // reavalia o tempo logo após viajar
  }

  /** Atualiza o clima. Devolve o clarão (0..1) para o Renderer. */
  update(dt: number, px: number, pz: number): number {
    this.timer -= dt;
    if (this.timer <= 0) this.roll();

    const want = this.kind === "clear" ? 0 : this.kind === "rain" ? 0.7 : 1;
    this.intensity += (want - this.intensity) * Math.min(1, dt * 0.6);
    this.mesh.visible = this.intensity > 0.03;
    (this.mesh.material as THREE.MeshBasicMaterial).opacity = 0.62 * this.intensity;

    if (this.mesh.visible) {
      const n = Math.max(1, Math.round(this.dropCap * this.intensity));
      for (let i = 0; i < this.dropCap; i++) {
        const d = this.drops[i];
        if (i >= n) {
          _m.position.set(0, -999, 0);
          _m.scale.setScalar(0.001);
          _m.rotation.set(0, 0, 0);
          _m.updateMatrix();
          this.mesh.setMatrixAt(i, _m.matrix);
          continue;
        }
        d.y -= d.sp * dt;
        if (d.y < 0) {
          d.y = randRange(14, 20);
          d.x = randRange(-BOX, BOX);
          d.z = randRange(-BOX, BOX);
        }
        _m.position.set(px + d.x, d.y, pz + d.z);
        _m.rotation.set(0.12, 0, 0.16); // gotas levemente inclinadas (vento)
        _m.scale.set(1, 1 + this.intensity * 0.8, 1);
        _m.updateMatrix();
        this.mesh.setMatrixAt(i, _m.matrix);
      }
      this.mesh.instanceMatrix.needsUpdate = true;
    }

    // relâmpago (só na tempestade)
    if (this.kind === "storm") {
      this.strikeIn -= dt;
      if (this.strikeIn <= 0) {
        this.strikeIn = randRange(4, 11);
        this.flash = 1;
      }
    }
    // 10.9BS — identidade da Tempestade: clarão distante, mais fraco que o da tempestade de verdade
    if (this.regionId === "tempestReach" && this.kind !== "storm") {
      this.ambientStrikeIn -= dt;
      if (this.ambientStrikeIn <= 0) {
        this.ambientStrikeIn = randRange(20, 40);
        this.flash = Math.max(this.flash, 0.55);
      }
    }
    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt * 3.2);
    return this.flash;
  }

  private roll(): void {
    const c = CHANCE[this.regionId] ?? CHANCE.forgottenForest;
    const r = Math.random();
    this.kind = r < c.storm ? "storm" : r < c.storm + c.rain ? "rain" : "clear";
    this.timer = this.kind === "clear" ? randRange(70, 150) : randRange(45, 95);
    this.strikeIn = randRange(2, 6);
  }
}
